/**
 * "You may also like" under a finished collection (snippets/bs-collection-picks).
 *
 * 1. Waits until the block is on screen - it starts hidden with the end-of-list marker and
 *    custom-pagination.js reveals it after the last page, so most collection views never call the app.
 * 2. Asks better-sell for picks: /apps/better-sell/api/collection-picks?collection=&parents=&limit=
 *    (app proxy; Shopify adds the logged-in customer, so the heading can say "Picked for you").
 * 3. Renders the theme's own product cards for all picks in ONE storefront search request
 *    (sections/bs-picks-results), restores the engine's order, and drops them into the theme's
 *    carousel-slider - so cards, quick add, badges and RTL match every other product row.
 * 4. Best-effort impression / click / add-to-cart tracking (surface "collection_end").
 * Any failure leaves the block empty and hidden; the page itself is never affected.
 */
if (!customElements.get('bs-collection-picks')) {
  const CHEVRON_RIGHT = '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m9.693 4.5 7.5 7.5-7.5 7.5" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';
  const CHEVRON_LEFT = '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m6.797 11.625 8.03-8.03 1.06 1.06-6.97 6.97 6.97 6.97-1.06 1.06z"/></svg>';
  const MIN_PICKS = 4;
  const SURFACE = 'collection_end';

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'
  }[c]));
  const numericId = (gid) => String(gid || '').split('/').pop();

  class BsCollectionPicks extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      const start = () => this.load().catch(() => this.clear());
      if (window.initLazyScript) window.initLazyScript(this, start, 400);
      else start();
    }

    clear() {
      this.innerHTML = '';
      this.classList.remove('is-ready');
    }

    track(body) {
      try {
        fetch(`${this.dataset.proxy}/api/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ surface: SURFACE, ...body }),
          keepalive: true
        });
      } catch (e) {
        /* tracking never breaks the storefront */
      }
    }

    async load() {
      const params = new URLSearchParams({
        collection: this.dataset.collection,
        parents: this.dataset.parents,
        limit: this.dataset.limit || '8'
      });
      const res = await fetch(`${this.dataset.proxy}/api/collection-picks?${params}`, {
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) return;
      const data = await res.json();
      const picks = ((data && data.products) || []).filter((p) => p && p.productId && p.handle);
      if (picks.length < MIN_PICKS) return;

      // One search request renders every card with the theme's product-card snippet
      const query = picks.map((p) => `id:${numericId(p.productId)}`).join(' OR ');
      const html = await fetch(`${this.dataset.resultsUrl}&q=${encodeURIComponent(query)}`)
        .then((r) => (r.ok ? r.text() : ''));
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const byId = new Map();
      tmpl.content.querySelectorAll('.bs-picks-results > li[data-product-id]').forEach((li) => {
        byId.set(li.dataset.productId, li);
      });

      const shown = [];
      const items = picks
        .map((p) => {
          const li = byId.get(numericId(p.productId));
          if (!li) return '';
          li.dataset.bsPid = p.productId;
          li.dataset.bsSource = p.source || '';
          shown.push(p);
          return li.outerHTML;
        })
        .join('');
      if (shown.length < MIN_PICKS) return;

      const heading = data.personalized ? this.dataset.headingPersonal : this.dataset.heading;
      const sliderId = `bs-picks-${this.dataset.collection}`;
      // Build the complete carousel before inserting it: carousel-slider reads its slides on upgrade.
      // No .container wrappers - the block already sits inside the collection grid's container.
      this.innerHTML = `
        <div class="mb-heading">
          <h2 class="h4 section__heading text-start">${esc(heading)}</h2>
        </div>
        <carousel-slider class="carousel block" inactive>
          <div class="relative">
            <div class="slider" id="${esc(sliderId)}">
              <div class="products-grid-container">
                <ul class="slider__grid grid grid-flow-col auto-cols-2 small-cards-mobile md:auto-cols-3 lg:auto-cols-4 xl:auto-cols-5 small-cards-desktop gap-x-theme gap-y-8" role="list">${items}</ul>
              </div>
            </div>
            <div class="slider-nav absolute top-middle end hidden md:block no-js-hidden">
              <button type="button" class="slider-nav__btn tap-target btn has-ltr-icon" name="next" aria-controls="${esc(sliderId)}">
                <span class="visually-hidden">${esc(this.dataset.nextLabel)}</span>${CHEVRON_RIGHT}
              </button>
              <button type="button" class="slider-nav__btn tap-target btn has-ltr-icon" name="prev" aria-controls="${esc(sliderId)}">
                <span class="visually-hidden">${esc(this.dataset.prevLabel)}</span>${CHEVRON_LEFT}
              </button>
            </div>
          </div>
        </carousel-slider>`;
      this.classList.add('is-ready');

      shown.forEach((p) => this.track({ eventType: 'IMPRESSION', productId: p.productId, source: p.source }));
      this.addEventListener('click', (evt) => {
        const item = evt.target.closest('[data-bs-pid]');
        if (!item) return;
        const body = { productId: item.dataset.bsPid, source: item.dataset.bsSource || undefined };
        if (evt.target.closest('[name="add"], .js-quick-add, quick-add-button, .quick-add-btn')) {
          this.track({ eventType: 'ACCEPTED', ...body });
        } else if (evt.target.closest('a[href*="/products/"]')) {
          this.track({ eventType: 'CLICK', ...body });
        }
      });
    }
  }

  customElements.define('bs-collection-picks', BsCollectionPicks);
}
